import { TNonEmptyArray } from 'src/types'

export type TLoadableIdle = {
  loading: false
  loaded: false
  data: null
  error: null
}

export type TLoadableLoading = {
  loading: true
  loaded: false
  data: null
  error: null
}

export type TLoadableSuccess<T> = {
  loading: false
  loaded: true
  data: T
  error: null
}

export type TLoadableFailure<E extends NonNullable<unknown> = Error> = {
  loading: false
  loaded: true
  data: null
  error: E
}

export type TLoadableGetError<T extends TLoadableAny> = Exclude<
  T['error'],
  null
>

export type TLoadableGetData<T extends TLoadableAny> = Exclude<T['data'], null>

export type TLoadable<T, E extends NonNullable<unknown> = Error> =
  | TLoadableIdle
  | TLoadableLoading
  | TLoadableSuccess<T>
  | TLoadableFailure<E>

export type TLoadableAny = TLoadable<unknown, NonNullable<unknown>>

const IDLE = Object.freeze({
  loading: false,
  loaded: false,
  data: null,
  error: null,
})

const LOADING = Object.freeze({
  loading: true,
  loaded: false,
  data: null,
  error: null,
})

const idleW = (): TLoadableIdle => IDLE

const idle = <T, E extends NonNullable<unknown> = Error>(): TLoadable<T, E> =>
  IDLE

const loadingW = (): TLoadableLoading => LOADING

const loading = <T, E extends NonNullable<unknown> = Error>(): TLoadable<
  T,
  E
> => LOADING

const successW = <T>(data: T): TLoadableSuccess<T> => ({
  loading: false,
  loaded: true,
  data,
  error: null,
})

const success: <T, E extends NonNullable<unknown> = Error>(
  data: T,
) => TLoadable<T, E> = successW

const failureW = <E extends NonNullable<unknown> = Error>(
  error: E,
): TLoadableFailure<E> => ({
  loading: false,
  loaded: true,
  data: null,
  error,
})
const failure: <T, E extends NonNullable<unknown> = Error>(
  error: E,
) => TLoadable<T, E> = failureW

const isSuccess = <T>(
  loadable: TLoadable<T, NonNullable<unknown>>,
): loadable is TLoadableSuccess<T> => {
  return loadable.loaded && loadable.error === null
}

const isFailure = <E extends NonNullable<unknown> = Error>(
  loadable: TLoadable<unknown, E>,
): loadable is TLoadableFailure<E> => {
  return loadable.error !== null
}

const isLoading = (
  loadable: TLoadable<unknown, NonNullable<unknown>>,
): loadable is TLoadableLoading => {
  return loadable.loading
}

const settled = <T, E extends NonNullable<unknown> = Error>(
  loadable: TLoadable<T, E>,
): loadable is TLoadableSuccess<T> | TLoadableFailure<E> => {
  return loadable.loaded
}

const isIdle = (loadable: TLoadableAny): loadable is TLoadableIdle => {
  return !loadable.loading && !loadable.loaded
}

export type TLoadableAll<
  T extends Record<string, TLoadableAny> = Record<string, TLoadableAny>,
> =
  | TLoadableIdle
  | TLoadableLoading
  | TLoadableFailure<
      TNonEmptyArray<
        {
          [K in keyof T]: [K, Exclude<T[K]['error'], null>]
        }[keyof T]
      >
    >
  | TLoadableSuccess<{
      [K in keyof T]: TLoadableGetData<T[K]>
    }>

export type TLoadableSettled<T extends TLoadableAny> = Extract<
  T,
  { loaded: true }
>

export type TLoadableAllSettled<T extends Record<string, TLoadableAny>> =
  | TLoadableIdle
  | TLoadableLoading
  | TLoadableSuccess<{
      [K in keyof T]: TLoadableSettled<T[K]>
    }>

export type TLoadableMapError<
  L extends TLoadableAny,
  U extends NonNullable<unknown>,
> = L extends TLoadableFailure<NonNullable<unknown>>
  ? TLoadableFailure<U>
  : Exclude<L, { error: NonNullable<unknown> }>

const all = <T extends Record<string, TLoadableAny>>(
  loadableDict: T,
): TLoadableAll<T> => {
  const loadables = Object.values(loadableDict)

  if (loadables.some(isFailure)) {
    return failure(
      Object.entries(loadableDict).reduce<
        Array<[string, NonNullable<unknown>]>
      >((acc, [key, loadable]) => {
        if (isFailure(loadable)) return acc
        acc[key] = loadable.error
        return acc
      }, []),
    ) as TLoadableAll<T>
  }

  if (loadables.some(e => e.loading)) {
    return loading()
  }

  if (loadables.every(isSuccess)) {
    return success(
      Object.fromEntries(
        Object.entries(loadableDict).map(([k, { data }]) => [k, data]),
      ),
    ) as TLoadableAll<T>
  }

  return idle()
}

const allSettled = <T extends Record<string, TLoadableAny>>(
  loadableDict: T,
): TLoadableAllSettled<T> => {
  const loadables = Object.values(loadableDict)

  if (loadables.some(isIdle)) return idleW()

  if (loadables.every(settled))
    return successW(
      loadableDict as NonNullable<TLoadableGetData<TLoadableAllSettled<T>>>,
    )

  return loadingW()
}

const fold =
  <T = unknown, E extends NonNullable<unknown> = Error>(
    loadable: TLoadable<T, E>,
  ) =>
  <B>({
    onIdle,
    onLoading,
    onFailure,
    onSuccess,
  }: {
    onIdle: () => B
    onLoading: () => B
    onFailure: (error: E) => B
    onSuccess: (data: T) => B
  }) => {
    if (isLoading(loadable)) return onLoading()
    if (isSuccess(loadable)) return onSuccess(loadable.data)
    if (isFailure(loadable)) return onFailure(loadable.error)
    return onIdle()
  }

export type TLoadableMap<
  L extends TLoadable<unknown, NonNullable<unknown>> = TLoadable<
    unknown,
    NonNullable<unknown>
  >,
  MappedData = unknown,
> = L extends TLoadableSuccess<unknown> ? TLoadableSuccess<MappedData> : L

export function map<
  L extends TLoadable<unknown, NonNullable<unknown>>,
  MappedData = unknown,
  _MapFnArg = L extends TLoadableSuccess<infer T> ? T : never,
>(
  loadable: L,
  mapFn: (data: _MapFnArg) => MappedData,
): TLoadableMap<L, MappedData> {
  if (!isSuccess(loadable)) return loadable as TLoadableMap<L, MappedData>

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    ...loadable,
    data: mapFn(loadable.data as _MapFnArg),
  } as TLoadableMap<L, MappedData>
}

export type TMapError<
  L,
  U extends NonNullable<unknown>,
> = L extends TLoadableFailure<NonNullable<unknown>> ? TLoadableFailure<U> : L

export function mapError<
  L extends TLoadable<unknown, NonNullable<unknown>>,
  U extends NonNullable<unknown>,
  _MapFnArg = L extends TLoadableFailure<infer E> ? E : never,
>(loadable: L, mapFn: (error: _MapFnArg) => U): TMapError<L, U> {
  if (loadable.error === null) return loadable as TMapError<L, U>

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return {
    ...loadable,
    error: mapFn(loadable.error as _MapFnArg),
  } as TMapError<L, U>
}

type TUnwrapOr<
  L extends TLoadable<unknown, NonNullable<unknown>>,
  Default,
> = L extends TLoadableSuccess<infer Data>
  ? Data
  : TLoadableGetData<L> | Default

export function unwrapOr<
  L extends TLoadable<unknown, NonNullable<unknown>>,
  Default,
>(loadable: L, defaultValue: Default): TUnwrapOr<L, Default> {
  if (!isSuccess(loadable)) return defaultValue as TUnwrapOr<L, Default>
  return loadable.data as TUnwrapOr<L, Default>
}

export default {
  idleW,
  idle,
  loadingW,
  loading,
  successW,
  success,
  failureW,
  failure,
  isSuccess,
  isFailure,
  isLoading,
  isIdle,
  settled,
  all,
  allSettled,
  fold,
  map,
  mapError,
  unwrapOr,
}
